import { ChangeEvent, useCallback, useRef, useState } from 'react';
import { AdminDatabaseExportPayload, AuthResponse, gameApi } from '../api';

interface AdminDebugPanelProps {
  currentGameId?: string;
  isAuthenticated: boolean;
  onOpenAdminGames: () => void;
  onOpenStatePerformance?: () => void;
  onAuthChanged: (auth: AuthResponse) => void;
  onMessage: (message: string) => void;
}

export const AdminDebugPanel: React.FC<AdminDebugPanelProps> = ({
  isAuthenticated,
  onOpenAdminGames,
  onOpenStatePerformance = () => undefined,
  onAuthChanged,
  onMessage,
}) => {
  const [adminError, setAdminError] = useState<string | null>(null);
  const [cacheResetLoading, setCacheResetLoading] = useState<boolean>(false);
  const [databaseTransferLoading, setDatabaseTransferLoading] = useState<boolean>(false);
  const [loginAsUserName, setLoginAsUserName] = useState<string>('');
  const [loginAsLoading, setLoginAsLoading] = useState<boolean>(false);
  const importFileInputRef = useRef<HTMLInputElement | null>(null);

  const handleResetV2Cache = useCallback(async () => {
    if (!isAuthenticated) {
      setAdminError('Login required');
      return;
    }

    setCacheResetLoading(true);
    setAdminError(null);
    try {
      await gameApi.resetV2GameStateCache();
      onMessage('V2 game state cache reset');
    } catch (err) {
      setAdminError(err instanceof Error ? err.message : 'Failed to reset v2 game state cache');
    } finally {
      setCacheResetLoading(false);
    }
  }, [isAuthenticated, onMessage]);

  const handleLoginAs = useCallback(async () => {
    if (!isAuthenticated) {
      setAdminError('Login required');
      return;
    }

    const targetUserName = loginAsUserName.trim();
    if (!targetUserName) {
      setAdminError('Username is required');
      return;
    }

    setLoginAsLoading(true);
    setAdminError(null);
    try {
      const auth = await gameApi.adminLoginAs(targetUserName);
      onAuthChanged(auth);
      onMessage(`Logged in as ${auth.userName}`);
    } catch (err) {
      setAdminError(err instanceof Error ? err.message : 'Failed to login as user');
    } finally {
      setLoginAsLoading(false);
    }
  }, [isAuthenticated, loginAsUserName, onAuthChanged, onMessage]);

  const handleExportDatabase = useCallback(async () => {
    if (!isAuthenticated) {
      setAdminError('Login required');
      return;
    }

    setDatabaseTransferLoading(true);
    setAdminError(null);
    try {
      const payload = await gameApi.exportDatabase();
      const json = JSON.stringify(payload, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      const exportStamp = new Date().toISOString().replace(/[:.]/g, '-');
      anchor.href = url;
      anchor.download = `arkham2-db-export-${exportStamp}.json`;
      anchor.click();
      window.URL.revokeObjectURL(url);
      onMessage('Database export downloaded');
    } catch (err) {
      setAdminError(err instanceof Error ? err.message : 'Failed to export database');
    } finally {
      setDatabaseTransferLoading(false);
    }
  }, [isAuthenticated, onMessage]);

  const handleOpenImportDialog = useCallback(() => {
    if (!isAuthenticated) {
      setAdminError('Login required');
      return;
    }

    importFileInputRef.current?.click();
  }, [isAuthenticated]);

  const handleImportFileSelected = useCallback(async (event: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] ?? null;
    event.target.value = '';
    if (!selectedFile) {
      return;
    }

    setDatabaseTransferLoading(true);
    setAdminError(null);
    try {
      const fileText = await selectedFile.text();
      const payload = JSON.parse(fileText) as AdminDatabaseExportPayload;
      const result = await gameApi.importDatabase(payload);
      onMessage(result.message);
    } catch (err) {
      if (err instanceof SyntaxError) {
        setAdminError('Failed to parse selected JSON file');
      } else {
        setAdminError(err instanceof Error ? err.message : 'Failed to import database');
      }
    } finally {
      setDatabaseTransferLoading(false);
    }
  }, [onMessage]);

  return (
    <div className="admin-panel">
      <h4>Admin Debug</h4>
      <div className="admin-actions">
        <button onClick={onOpenAdminGames}>
          All Games
        </button>
        <button onClick={onOpenStatePerformance}>
          State Performance
        </button>
        <input
          aria-label="Login as username"
          type="text"
          value={loginAsUserName}
          onChange={(event) => setLoginAsUserName(event.target.value)}
          placeholder="Username"
        />
        <button
          onClick={handleLoginAs}
          disabled={loginAsLoading || !loginAsUserName.trim()}
        >
          Login As
        </button>
        <button onClick={handleResetV2Cache} disabled={cacheResetLoading}>
          Reset V2 Cache
        </button>
        <button onClick={handleExportDatabase} disabled={databaseTransferLoading}>
          Export DB
        </button>
        <button onClick={handleOpenImportDialog} disabled={databaseTransferLoading}>
          Import DB
        </button>
        <input
          ref={importFileInputRef}
          type="file"
          accept="application/json"
          onChange={handleImportFileSelected}
          style={{ display: 'none' }}
        />
      </div>
      {adminError && <div className="error">{adminError}</div>}
    </div>
  );
};
